import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, ScrollTrigger, prefersReducedMotion } from '@/lib/gsap'
import { studioMeta } from '@/constants/site'

interface WordmarkProps {
    className?: string
    size?: string
    tone?: 'dark' | 'light'
    animate?: boolean
}

/* The full lockup: the wordmark set huge, the optional mark hung off its
   top-right corner (skipped while studioMeta.mark is empty), and the script descriptor tucked under the last
   letter in the accent colour. */
export const Wordmark: FC<WordmarkProps> = ({
    className,
    size = 'clamp(52px,10vw,150px)',
    tone = 'dark',
    animate = true
}): ReactNode => {
    const scope = useRef<HTMLDivElement>(null)

    useGsap((self) => {
        const root = scope.current

        if (!root || !animate || prefersReducedMotion()) {
            return
        }

        const letters = root.querySelectorAll('[data-wordmark-letter]')
        const mark = root.querySelector('[data-wordmark-mark]')
        const script = root.querySelector('[data-wordmark-script]')

        /* The letters sit in their visible place in the markup and are only
           parked below the mask at the moment the reveal starts. So if the
           trigger never fires, nothing is left hidden. It starts as soon as
           the wordmark's top edge enters the viewport, which the bottom of
           the page can always reach. A standalone trigger measures at once,
           unlike one attached to a timeline. */
        ScrollTrigger.create({
            trigger: root,
            start: 'top bottom',
            once: true,
            onEnter: () => {
                self.add(() => {
                    const tl = gsap.timeline()

                    tl.fromTo(letters,
                        { yPercent: 150 },
                        { yPercent: 0, duration: 1.15, stagger: 0.055, ease: 'noma' },
                        0
                    )

                    if (mark) {
                        tl.fromTo(mark,
                            { opacity: 0, scale: 0.4, rotate: -30 },
                            { opacity: 1, scale: 1, rotate: 0, duration: 0.9, ease: 'noma' },
                            0.35
                        )
                    }

                    if (script) {
                        gsap.set(script, { opacity: 0, x: -34, rotate: -7 })
                        tl.to(script,
                            { opacity: 1, x: 0, rotate: 0, duration: 1.1, ease: 'noma' },
                            0.45
                        )
                    }
                })
            }
        })
    }, scope, [animate])

    return (
        <div ref={scope} className={cn('relative w-fit', className)}>
            <span
                className={cn(
                    't-display flex overflow-hidden py-[0.22em] -my-[0.22em] leading-[0.8]',
                    tone === 'dark' ? 'text-ink' : 'text-paper'
                )}
                style={{ fontSize: size }}
            >
                {Array.from('strength').map((letter, index) => (
                    <span
                        key={`${letter}-${index}`}
                        data-wordmark-letter
                        className='inline-block'
                    >
                        {letter}
                    </span>
                ))}
            </span>

            {studioMeta.mark
                ? (
                    <span
                        data-wordmark-mark
                        className={cn(
                            't-display absolute left-[98%] top-[12%] leading-[0.7]',
                            tone === 'dark' ? 'text-ink' : 'text-paper'
                        )}
                        style={{ fontSize: `calc(${size} * 0.177)` }}
                        aria-hidden='true'
                    >
                        {studioMeta.mark}
                    </span>
                )
                : null}

            <span
                data-wordmark-script
                className={cn(
                    't-script absolute bottom-0 right-0 translate-y-[26%] uppercase leading-[0.62] text-[#0066FF]'
                )}
                style={{ fontSize: `calc(${size} * 0.336)` }}
                aria-hidden='true'
            >
                Scaling
            </span>
        </div>
    )
}
